import React from 'react';
import FaIcon from './FaIcon.js';
import { getUserGuidePath } from '../userGuides';

interface UserGuideTitleProps {
  // The page's menu title, used to find its guide (see userGuides.js); shown unless children are given.
  title: string;
  // Optional url slug that disambiguates menu entries sharing a display label.
  url?: string;
  children?: React.ReactNode;
}

// A page title that opens the page's user guide in a new tab, with a small question-mark affordance
// just to the right of the text. Renders the plain title when the page has no guide. Used by the
// shell header (content.js) and by pages that hide the shell header to draw their own page head.
function UserGuideTitle({ title, url, children }: UserGuideTitleProps) {
  const text = children ?? title;
  const guidePath = getUserGuidePath({ title, url });
  if (!guidePath) {
    return <>{text}</>;
  }

  const openGuide = () => window.open(guidePath, '_blank', 'noopener');
  const onGuideKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openGuide();
    }
  };

  return (
    <span
      role='button'
      tabIndex={0}
      title='Open the user guide for this page'
      onClick={openGuide}
      onKeyDown={onGuideKeyDown}
      style={{ cursor: 'pointer' }}
    >
      {text}
      <span
        aria-hidden='true'
        style={{
          fontSize: '0.7em',
          marginLeft: '0.35em',
          verticalAlign: 'middle',
          color: '#8b96a1',
          opacity: 0.7,
          fontWeight: 400,
        }}
      >
        <FaIcon iconName='fa-regular fa-circle-question' />
      </span>
    </span>
  );
}

export default UserGuideTitle;
